import Building from "@icons/building.svg?url";
import Cross from "@icons/cross.svg?react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useSuspenseQuery } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";

import { ActionGuard } from "@/common/acl/action-guard/action-guard";
import { useCompany } from "@/common/providers/company-provider";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { useTokenUpdate } from "@/common/providers/token-provider";
import { splitCallingCodeAndCountry } from "@/common/utils/phone";
import { languagesQuery } from "@/routes/admin/editor/editor.api";
import { FullscreenModalLayout } from "@/routes/layout/layout.fullscreen";
import { Failed } from "@/routes/shared/routes/failed/failed";
import { Success } from "@/routes/shared/routes/success/success";

import { Button } from "../button/button";
import { InputField } from "../form/input-field/input-field";
import { PhoneNumber } from "../form/phone-number/phone-number";
import { CompanyForm } from "./components/company-form/company-form";
import { useCountryCode } from "./hooks/use-country-code";
import {
  appLanguagesQuery,
  useUpdateUserSettingsMutation,
} from "./user-settings.api";

type Fields = {
  username: string;
  email: string;
  phone: {
    countryCode: string;
    phoneNumber: string;
  };
  newsLanguage: string;
  language: string;
};

const UserSettings = () => {
  const sidebar = useSidebar();
  const [isTokenUpdating, updateToken] = useTokenUpdate();
  const { user, company } = useCompany();
  const appLanguages = useSuspenseQuery(appLanguagesQuery());
  const marketNewsLanguages = useSuspenseQuery(languagesQuery());
  const { current: countryCode, phoneCountryCodes } = useCountryCode(
    user.language,
  );
  const updateUser = useUpdateUserSettingsMutation();
  const form = useForm<Fields>({
    defaultValues: {
      email: user.email,
      language: user.language,
      newsLanguage: user.newsLanguage,
      phone: {
        countryCode: countryCode,
        phoneNumber: user.phone.phoneNumber,
      },
      username: user.name,
    },
  });

  const handleSubmit = (data: Fields) => {
    updateUser.mutate(
      {
        language: data.language,
        name: data.username,
        newsLanguage: data.newsLanguage,
        phone: {
          countryCode: splitCallingCodeAndCountry(data.phone.countryCode)[0],
          phoneNumber: data.phone.phoneNumber,
        },
      },
      {
        onError: () => {
          sidebar.actions.openScreen("user-failure");
        },
        onSuccess: async () => {
          await updateToken();
          sidebar.actions.openScreen("user-success");
        },
      },
    );
  };

  if (sidebar.state.screen === "user-failure") {
    return (
      <Failed
        action={
          <Button onClick={sidebar.actions.executeNextAction} variant="primary">
            <Trans>Přejít na profil</Trans>
          </Button>
        }
        content={
          <Trans>
            Bohužel někde nastala chyba a údaje se nepodařilo uložit. Zkuste to
            později.
          </Trans>
        }
        headerButton={
          <button onClick={sidebar.actions.close}>
            <Cross />
          </button>
        }
        title={<Trans>Údaje se nepodařilo uložit</Trans>}
      />
    );
  }

  if (sidebar.state.screen === "user-success") {
    return (
      <Success
        action={
          <Button onClick={sidebar.actions.executeNextAction} variant="primary">
            <Trans>Přejít na profil</Trans>
          </Button>
        }
        content={
          <Success.Content>
            <Trans>
              Údaje jsme úspěšně uložili. Více informací najdete ve vašem
              profilu.
            </Trans>
          </Success.Content>
        }
        headerButton={
          <button onClick={sidebar.actions.close}>
            <Cross />
          </button>
        }
        title={
          <Success.Title>
            <Trans>Údaje úspěšně uloženy</Trans>
          </Success.Title>
        }
      />
    );
  }

  if (sidebar.state.screen === "user-company") {
    return (
      <ActionGuard
        grant="COMPANY_DETAIL_EDIT"
        guard={{
          action: <Trans>Zavřít</Trans>,
          content: [
            <Trans key="key-no-privilege">Nemáte dostatečné oprávnění.</Trans>,
            <Trans key="contact-support">
              Kontaktujte podporu{" "}
              <a href="mailto:help@cleverfarm.ag">help@cleverfarm.ag</a>.
            </Trans>,
          ],
          title: <Trans>Akci nelze dokončit</Trans>,
        }}
      >
        <CompanyForm />
      </ActionGuard>
    );
  }

  return (
    <FullscreenModalLayout
      headerButton={
        <button onClick={sidebar.actions.toggle}>
          <Cross />
        </button>
      }
    >
      <FullscreenModalLayout.Title>
        <Trans>Profil</Trans>
      </FullscreenModalLayout.Title>
      <FormProvider {...form}>
        <form
          className="flex flex-col gap-y-6"
          id="user-settings-form"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <InputField<Fields>
            label={<Trans>Jméno a příjmení</Trans>}
            name="username"
            options={{
              required: t`Vyplňte prosím jméno a příjmení`,
            }}
          />

          <InputField<Fields>
            disabled
            label={<Trans>E-mail</Trans>}
            name="email"
          />

          <PhoneNumber callingCodes={phoneCountryCodes.data} name="phone" />

          <label className="group" htmlFor="app-language">
            <Trans>Jazyk aplikace</Trans>
            <select
              className="mt-2 block w-full rounded-lg border p-4 font-bold group-has-[[role=alert]]:border-can-russet"
              id="app-language"
              required
              {...form.register("language")}
            >
              {appLanguages.data.map((language) => (
                <option key={language.id} value={language.id}>
                  {language.localName}
                </option>
              ))}
            </select>
          </label>
          <h2 className="font-bold text-can-forest-teal">
            <Trans>Market news</Trans>
          </h2>
          <p>
            <Trans>
              Nastavte si jazyk, ve kterém chcete dostávat týdenní informace o
              událostech na trhu.
            </Trans>
          </p>

          <label className="group" htmlFor="market-news">
            <Trans>Jazyk aktualit</Trans>
            <select
              className="mt-2 block w-full rounded-lg border p-4 font-bold group-has-[[role=alert]]:border-can-russet"
              id="market-news"
              required
              {...form.register("newsLanguage")}
            >
              {marketNewsLanguages.data.map((language) => (
                <option key={language.id} value={language.id}>
                  {language.localName}
                </option>
              ))}
            </select>
          </label>
        </form>
      </FormProvider>
      <Button.Container>
        {company ? (
          <Button
            onClick={sidebar.actions.executeNextAction}
            style={{
              // eslint-disable-next-line lingui/no-unlocalized-strings
              background: `url(${Building}) left 16px center no-repeat`,
            }}
            variant="secondary"
          >
            <Trans>Informace o společnosti</Trans>
          </Button>
        ) : null}
        <Button
          disabled={updateUser.status === "pending" || isTokenUpdating}
          form="user-settings-form"
          type="submit"
          variant="primary"
        >
          <Trans>Uložit</Trans>
        </Button>
      </Button.Container>
    </FullscreenModalLayout>
  );
};

export { UserSettings };
